<template>
  <multiselect
    class="entidade-autocomplete"
    :value="internalValue"
    :options="opcoes"
    :multiple="multiple"
    :loading="loading"
    :disabled="disabled"
    :placeholder="placeholder || placeholderPadrao"
    :searchable="true"
    :internal-search="false"
    :close-on-select="!multiple"
    :clear-on-select="false"
    :preserve-search="true"
    :show-labels="false"
    track-by="id"
    label="descricao"
    @search-change="onSearch"
    @open="onOpen"
    @input="onInput"
  >
    <template slot="singleLabel" slot-scope="{ option }">
      <IdDescricaoLabel :id="option.id" :descricao="option.descricao" />
    </template>
    <template slot="option" slot-scope="{ option }">
      <IdDescricaoLabel :id="option.id" :descricao="option.descricao" />
    </template>
    <template slot="tag" slot-scope="{ option, remove }">
      <span class="ea-tag">
        <IdDescricaoLabel :id="option.id" :descricao="option.descricao" />
        <span class="ea-tag__remove" @click.stop="remove(option)">&times;</span>
      </span>
    </template>
    <template slot="noOptions">
      <span class="ea-empty">Digite para buscar...</span>
    </template>
    <template slot="noResult">
      <span class="ea-empty">Nenhum resultado encontrado.</span>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { get } from "@/helpers/apiRequest";
import IdDescricaoLabel from "./IdDescricaoLabel.vue";

const ENDPOINTS = {
  cliente: "/admin/clientes",
  colaborador: "/admin/colaboradores",
  produto: "/admin/lista-produtos",
  marca: "/admin/marca",
  operacao: "/admin/operacoes",
  transportadora: "/admin/transportadoras",
  linkIntegrado: "/admin/marketing/links-integracao",
};

const PLACEHOLDERS = {
  cliente: "Buscar cliente...",
  colaborador: "Buscar colaborador...",
  produto: "Buscar produto...",
  marca: "Buscar marca...",
  operacao: "Buscar operacao...",
  transportadora: "Buscar transportadora...",
  linkIntegrado: "Buscar link integrado...",
};

function normalizeItem(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object") return { id: raw, descricao: String(raw) };

  const id = raw.id != null ? raw.id : raw.codigo != null ? raw.codigo : raw.value;
  const descricao =
    raw.descricao ||
    raw.nome ||
    raw.razao_social ||
    raw.titulo ||
    raw.label ||
    raw.fantasia ||
    "";
  return { id, descricao, _raw: raw };
}

function extractList(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.items)) return json.items;
  if (Array.isArray(json.results)) return json.results;
  return [];
}

export default {
  name: "EntidadeAutocomplete",
  components: { IdDescricaoLabel, Multiselect },
  props: {
    value: {
      type: [String, Number, Array],
      default: null,
    },
    entidade: {
      type: String,
      required: true,
      validator: (v) => Object.keys(ENDPOINTS).includes(v),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * Descricao do valor ja selecionado, para exibir o rotulo correto na edicao
     * sem precisar carregar a lista inteira so para resolver um id.
     */
    valueLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      opcoes: [],
      cacheById: {},
      loading: false,
      debounceTimer: null,
      ultimoTermo: "",
    };
  },
  computed: {
    placeholderPadrao() {
      return PLACEHOLDERS[this.entidade] || "Buscar...";
    },
    internalValue() {
      if (this.multiple) {
        const ids = Array.isArray(this.value) ? this.value : [];
        return ids.map((id) => this.cacheById[id] || { id, descricao: "" });
      }
      if (this.value === null || this.value === undefined || this.value === "") return null;
      return this.cacheById[this.value] || { id: this.value, descricao: this.valueLabel };
    },
  },
  methods: {
    endpoint() {
      return ENDPOINTS[this.entidade];
    },
    onOpen() {
      if (!this.opcoes.length) {
        this.fetch("");
      }
    },
    onSearch(termo) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetch(termo || "");
      }, 300);
    },
    fetch(termo) {
      const url = this.endpoint();
      if (!url) return;
      this.ultimoTermo = termo;
      this.loading = true;
      const sep = url.includes("?") ? "&" : "?";
      const qs = termo ? `${sep}q=${encodeURIComponent(termo)}&take=20` : `${sep}take=20`;

      get(`${url}${qs}`)
        .then((json) => {
          if (this.ultimoTermo !== termo) return;
          const lista = extractList(json)
            .map(normalizeItem)
            .filter((i) => i && i.id != null);
          this.opcoes = lista;
          lista.forEach((item) => {
            this.$set(this.cacheById, item.id, item);
          });
        })
        .catch(() => {
          // 404 ou endpoint ainda nao implementado -> degradar graciosamente
          this.opcoes = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onInput(val) {
      if (this.multiple) {
        const arr = Array.isArray(val) ? val : [];
        arr.forEach((item) => {
          if (item && item.id != null) this.$set(this.cacheById, item.id, item);
        });
        this.$emit("input", arr.map((i) => i.id));
      } else {
        if (val && val.id != null) this.$set(this.cacheById, val.id, val);
        this.$emit("input", val ? val.id : null);
      }
    },
  },
};
</script>

<style scoped>
.entidade-autocomplete {
  font-size: 13px;
}

/* ----- PALETA DAS OPÇÕES -----

   O vue-multiselect pinta a opção sob o cursor de verde (#41b883) e a opção já
   selecionada de vermelho (#ff6a6a, o "press enter to remove"), cores que não
   pertencem ao painel. O `:after` também precisa entrar: com show-labels
   desligado ele fica sem texto, mas mantém padding e continua pintando uma
   faixa colorida à direita da opção.

   Seleção usa o #c5c8cc já empregado no restante do painel; o destaque fica
   neutro, na cor primária. */
::v-deep .multiselect__option--highlight,
::v-deep .multiselect__option--highlight:after {
  background: #f7f7fc;
  color: #3a3e81;
}

::v-deep .multiselect__option--selected,
::v-deep .multiselect__option--selected:after,
::v-deep .multiselect__option--selected.multiselect__option--highlight,
::v-deep .multiselect__option--selected.multiselect__option--highlight:after {
  background: #c5c8cc;
  color: #344054;
}

.ea-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  background: #e3e3ee;
  color: #3a3e81;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin: 2px 4px 2px 0;
}

.ea-tag__remove {
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  opacity: 0.7;
}

.ea-tag__remove:hover {
  opacity: 1;
}

.ea-empty {
  color: #98a2b3;
  font-size: 12px;
}
</style>
